import { Controller } from "stimulus";

// Connects to data-controller="industry-filter"
export default class extends Controller {
  static targets = ["filterButton", "article", "scopeButton", "headingFilterButton", "subheadingFilterButton", "sectorToggle", "industryList", "noMatchesMessage", "articlesContent", "companyResearchFilterButton", "titleColorFilterButton", "titleColorFilterSection", "countryFilterButton", "countryFilterGroup"];

  connect() {
    console.log("IndustryFilterController connected");
    this.activeFilters = new Set();
    this.activeHeadingFilters = new Set();
    this.activeSubheadingFilters = new Set();
    this.activeCountryFilters = new Map();
    this.activeTitleColorFilters = new Set();
    this.activeCompanyResearchFilter = false;
    
    const urlParams = new URLSearchParams(window.location.search);
    
    // Check if we're in a search state (search_cut parameter present)
    if (urlParams.has('search_cut')) {
      this.searchScope = "all"; // Set to "all" when search_cut parameter is present
      this.updateScopeButtons("all");
      
      // Mark the active filter based on the search term
      const searchTerm = urlParams.get('search_cut');
      this.markActiveFilterFromSearch(searchTerm);
    } 
    // Check if we need to apply a filter in current edition (coming from All to Current)
    else if (urlParams.has('apply_filter')) {
      this.searchScope = "current";
      this.updateScopeButtons("current");
      
      // Apply the filter from the URL parameter
      const filterTerm = urlParams.get('apply_filter');
      this.applyFilterInCurrentEdition(filterTerm);
      
      // Clean up the URL by removing the apply_filter parameter
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('apply_filter');
      window.history.replaceState({}, '', newUrl);
    } 
    else {
      this.searchScope = "current"; // default to current edition
      this.updateScopeButtons("current");
    }
    
    // Check if industry dropdown should be open based on active filters
    this.checkIndustryDropdownState();
  }

  toggleScope(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const scope = button.dataset.scope;
    
    // If clicking "Current" while in "All" mode with search results, preserve the filter
    const urlParams = new URLSearchParams(window.location.search);
    if (scope === "current" && this.searchScope === "all" && urlParams.has('search_cut')) {
      // Get the search term and redirect to current edition with it applied as a filter
      const searchTerm = urlParams.get('search_cut');
      const currentPath = window.location.pathname;
      
      // Check if the search term corresponds to a filter we can apply
      const canApplyFilter = this.canApplyFilterInCurrentEdition(searchTerm);
      
      if (canApplyFilter) {
        // Redirect to current edition page and let the connect() method handle filter application
        window.location.href = `${currentPath}?apply_filter=${encodeURIComponent(searchTerm)}`;
      } else {
        // Redirect to clean current edition page if filter can't be applied
        window.location.href = currentPath;
      }
      return;
    }
    
    // If clicking "All" while in "Current" mode with active filters, redirect to global search
    if (scope === "all" && this.searchScope === "current") {
      const hasActiveFilters = this.activeFilters.size > 0 || 
                              this.activeHeadingFilters.size > 0 || 
                              this.activeSubheadingFilters.size > 0 ||
                              this.activeTitleColorFilters.size > 0 ||
                              this.activeCompanyResearchFilter;
      
      if (hasActiveFilters) {
        // Get the first active filter to search for
        let searchTerm = null;
        
        if (this.activeFilters.size > 0) {
          // Use industry filter
          searchTerm = Array.from(this.activeFilters)[0];
        } else if (this.activeTitleColorFilters.size > 0) {
          // Convert the local title color back to its Cut search label.
          const titleColor = Array.from(this.activeTitleColorFilters)[0];
          searchTerm = titleColor === "red" ? "Sells" : "Buys";
        } else if (this.activeCompanyResearchFilter) {
          searchTerm = "Company Research";
        } else if (this.activeSubheadingFilters.size > 0) {
          // Use subheading filter - find the button to get the title
          const subheadingId = Array.from(this.activeSubheadingFilters)[0];
          const subheadingButton = this.subheadingFilterButtonTargets.find(btn => 
            btn.dataset.subheadingId === subheadingId
          );
          if (subheadingButton) {
            searchTerm = subheadingButton.textContent.trim();
          }
        } else if (this.activeHeadingFilters.size > 0) {
          // Use heading filter - find the button to get the title
          const headingId = Array.from(this.activeHeadingFilters)[0];
          const headingButton = this.headingFilterButtonTargets.find(btn => 
            btn.dataset.headingId === headingId
          );
          if (headingButton) {
            searchTerm = headingButton.textContent.trim();
          }
        }
        
        if (searchTerm) {
          this.redirectToGlobalSearch(searchTerm);
          return;
        }
      }
    }
    
    this.searchScope = scope;
    this.updateScopeButtons(scope);
    
    // Clear current filters and reapply based on new scope
    this.clearAllFilters();
    
    // Ensure all buttons are properly enabled after scope change
    this.enableAllFilterButtons();
  }

  updateScopeButtons(scope) {
    // Update active scope button classes based on the current scope
    this.scopeButtonTargets.forEach(btn => {
      if (btn.dataset.scope === scope) {
        btn.classList.remove("nav-link-btn-unselected");
        btn.classList.add("nav-link-btn-selected");
        btn.classList.remove("disabled");
      } else {
        btn.classList.remove("nav-link-btn-selected");
        btn.classList.add("nav-link-btn-unselected");
        btn.classList.remove("disabled");
      }
    });
    
    // Update filter button states based on scope
    this.updateFilterButtonStates(scope);
    this.updateTitleColorFilterVisibility(scope);
  }

  updateTitleColorFilterVisibility(scope) {
    if (!this.hasTitleColorFilterSectionTarget) return;

    this.titleColorFilterSectionTarget.style.display = "";
  }

  markActiveFilterFromSearch(searchTerm) {
    if (!searchTerm) return;
    
    // Decode the search term in case it's URL encoded
    const decodedSearchTerm = decodeURIComponent(searchTerm).toLowerCase();
    
    // Check if it's an industry filter
    this.filterButtonTargets.forEach(btn => {
      const industry = btn.dataset.industry;
      if (industry && industry.toLowerCase() === decodedSearchTerm) {
        btn.classList.add("active");
        this.activeFilters.add(industry);
      }
    });
    
    // Check if it's a heading filter
    if (this.headingFilterButtonTargets) {
      this.headingFilterButtonTargets.forEach(btn => {
        const headingTitle = btn.textContent.trim().toLowerCase();
        if (headingTitle === decodedSearchTerm) {
          btn.classList.add("active");
          this.activeHeadingFilters.add(btn.dataset.headingId);
        }
      });
    }
    
    // Check if it's a subheading filter
    if (this.subheadingFilterButtonTargets) {
      this.subheadingFilterButtonTargets.forEach(btn => {
        const subheadingTitle = btn.textContent.trim().toLowerCase();
        if (subheadingTitle === decodedSearchTerm) {
          btn.classList.add("active");
          this.activeSubheadingFilters.add(btn.dataset.subheadingId);
        }
      });
    }
    
    // Check if it's company research filter
    if (decodedSearchTerm === "company research") {
      this.activeCompanyResearchFilter = true;
      if (this.companyResearchFilterButtonTargets.length > 0) {
        this.companyResearchFilterButtonTargets[0].classList.add("active");
      }
    }

    this.markActiveTitleColorFilter(decodedSearchTerm);
  }

  checkIndustryDropdownState() {
    // If there are active industry filters, ensure the dropdown is open
    if (this.activeFilters.size > 0 && this.hasIndustryListTarget) {
      this.openIndustryDropdown();
    }
  }

  openIndustryDropdown() {
    if (this.hasIndustryListTarget) {
      // Show the industry list
      this.industryListTarget.removeAttribute('hidden');
      this.industryListTarget.classList.add('d-flex');
      this.industryListTarget.style.display = '';
      
      // Update arrow direction to point up
      if (this.hasSectorToggleTarget) {
        const arrow = this.sectorToggleTarget.querySelector('i');
        if (arrow) {
          arrow.classList.remove("fa-chevron-down");
          arrow.classList.add("fa-chevron-up");
        }
      }
    }
  }

  updateFilterButtonStates(scope) {
    // Enable/disable filter buttons based on scope
    // In "all" mode, filters redirect to global search
    // In "current" mode, filters work locally
    const isAllMode = scope === "all";
    
    // Update industry filter buttons
    this.filterButtonTargets.forEach(btn => {
      if (isAllMode) {
        // In "all" mode, show that these will redirect (they're still clickable)
        btn.style.opacity = "1";
        btn.style.cursor = "pointer";
        btn.style.pointerEvents = "auto";
      } else {
        // In "current" mode, these work as normal local filters
        btn.style.opacity = "1";
        btn.style.cursor = "pointer";
        btn.style.pointerEvents = "auto";
      }
    });
    
    // Update heading and subheading filter buttons
    if (this.headingFilterButtonTargets) {
      this.headingFilterButtonTargets.forEach(btn => {
        btn.style.opacity = "1";
        btn.style.cursor = "pointer";
        btn.style.pointerEvents = "auto";
      });
    }
    
    if (this.subheadingFilterButtonTargets) {
      this.subheadingFilterButtonTargets.forEach(btn => {
        btn.style.opacity = "1";
        btn.style.cursor = "pointer";
        btn.style.pointerEvents = "auto";
      });
    }

    if (this.titleColorFilterButtonTargets) {
      this.titleColorFilterButtonTargets.forEach(btn => {
        btn.style.opacity = "1";
        btn.style.cursor = "pointer";
        btn.style.pointerEvents = "auto";
      });
    }
  }

  enableAllFilterButtons() {
    // Helper method to enable all filter buttons
    this.filterButtonTargets.forEach(btn => {
      btn.style.opacity = "1";
      btn.style.cursor = "pointer";
      btn.style.pointerEvents = "auto";
    });
    
    if (this.headingFilterButtonTargets) {
      this.headingFilterButtonTargets.forEach(btn => {
        btn.style.opacity = "1";
        btn.style.cursor = "pointer";
        btn.style.pointerEvents = "auto";
      });
    }
    
    if (this.subheadingFilterButtonTargets) {
      this.subheadingFilterButtonTargets.forEach(btn => {
        btn.style.opacity = "1";
        btn.style.cursor = "pointer";
        btn.style.pointerEvents = "auto";
      });
    }

    if (this.titleColorFilterButtonTargets) {
      this.titleColorFilterButtonTargets.forEach(btn => {
        btn.style.opacity = "1";
        btn.style.cursor = "pointer";
        btn.style.pointerEvents = "auto";
      });
    }
  }

  clearAllActiveFilters() {
    // Helper method to clear all active filter states
    this.activeFilters.clear();
    this.activeHeadingFilters.clear();
    this.activeSubheadingFilters.clear();
    this.activeCountryFilters.clear();
    this.countryFilterButtonTargets.forEach(btn => {
      btn.classList.remove("active");
      btn.setAttribute("aria-pressed", "false");
    });
    this.activeTitleColorFilters.clear();
    this.activeCompanyResearchFilter = false;
    
    // Remove active classes from all filter buttons
    this.filterButtonTargets.forEach(btn => {
      btn.classList.remove("active");
      btn.setAttribute("aria-pressed", "false");
    });
    
    if (this.headingFilterButtonTargets) {
      this.headingFilterButtonTargets.forEach(btn => {
        btn.classList.remove("active");
        btn.setAttribute("aria-pressed", "false");
      });
    }
    
    if (this.subheadingFilterButtonTargets) {
      this.subheadingFilterButtonTargets.forEach(btn => {
        btn.classList.remove("active");
        btn.setAttribute("aria-pressed", "false");
      });
    }
    
    if (this.companyResearchFilterButtonTargets) {
      this.companyResearchFilterButtonTargets.forEach(btn => {
        btn.classList.remove("active");
        btn.setAttribute("aria-pressed", "false");
      });
    }

    if (this.titleColorFilterButtonTargets) {
      this.titleColorFilterButtonTargets.forEach(btn => {
        btn.classList.remove("active");
        btn.setAttribute("aria-pressed", "false");
      });
    }
  }



  toggleIndustryList(event) {
    event.preventDefault();
    
    if (this.hasIndustryListTarget) {
      // Check if the element is currently hidden
      const isHidden = this.industryListTarget.hasAttribute('hidden') || this.industryListTarget.style.display === 'none';
      
      // Toggle visibility using both hidden attribute and Bootstrap classes
      if (isHidden) {
        this.industryListTarget.removeAttribute('hidden');
        this.industryListTarget.classList.add('d-flex');
        this.industryListTarget.style.display = '';
      } else {
        this.industryListTarget.setAttribute('hidden', '');
        this.industryListTarget.classList.remove('d-flex');
        this.industryListTarget.style.display = 'none';
      }
      
      // Update arrow direction
      if (this.hasSectorToggleTarget) {
        const arrow = this.sectorToggleTarget.querySelector('i');
        if (arrow) {
          if (isHidden) {
            // Showing the list, arrow should point up
            arrow.classList.remove("fa-chevron-down");
            arrow.classList.add("fa-chevron-up");
          } else {
            // Hiding the list, arrow should point down
            arrow.classList.remove("fa-chevron-up");
            arrow.classList.add("fa-chevron-down");
          }
        }
      }
    }
  }

  toggleFilter(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const industry = button.dataset.industry;
    
    if (this.searchScope === "all") {
      // For all editions scope, redirect to global search
      this.redirectToGlobalSearch(industry);
      return;
    }
    
    this.toggleSelection(this.activeFilters, industry, button);
    
    this.applyAllFilters();
    
    // Check if industry dropdown should be open based on active filters
    this.checkIndustryDropdownState();
  }

  redirectToGlobalSearch(searchTerm) {
    const urlPath = window.location.pathname;
    const editionMatch = urlPath.match(/\/(?:editions|cut|cut-new)\/(\d+)/);
    const currentEdition = editionMatch ? editionMatch[1] : null;
    
    let searchUrl;
    if (currentEdition) {
      if (urlPath.match(/\/cut-new\/\d+/)) {
        searchUrl = `/cut-new/${currentEdition}?search_cut=${encodeURIComponent(searchTerm)}&cut_filter=1`;
      } else if (urlPath.match(/\/cut\/\d+/)) {
        searchUrl = `/cut/${currentEdition}?search_cut=${encodeURIComponent(searchTerm)}&cut_filter=1`;
      } else {
        searchUrl = `/editions/${currentEdition}?search_cut=${encodeURIComponent(searchTerm)}`;
      }
    } else {
      searchUrl = `/editions?search_cut=${encodeURIComponent(searchTerm)}`;
    }
    
    window.location.href = searchUrl;
  }

  clearAllFilters() {
    this.clearAllActiveFilters();
    this.applyAllFilters();
    this.showContent();
    
    // Check if industry dropdown should be open (should close when no filters)
    this.checkIndustryDropdownState();
  }

  toggleHeadingFilter(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const headingId = button.dataset.headingId;
    const headingTitle = button.textContent.trim();
    
    if (this.searchScope === "all") {
      // For all editions scope, redirect to global search with heading title
      this.redirectToGlobalSearch(headingTitle);
      return;
    }
    
    this.toggleSelection(this.activeHeadingFilters, headingId, button);
    
    this.applyAllFilters();
  }

  toggleSubheadingFilter(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const subheadingId = button.dataset.subheadingId;
    const subheadingTitle = button.textContent.trim();
    
    if (this.searchScope === "all") {
      // For all editions scope, redirect to global search with subheading title
      this.redirectToGlobalSearch(subheadingTitle);
      return;
    }
    
    this.toggleSelection(this.activeSubheadingFilters, subheadingId, button);
    if (!this.activeSubheadingFilters.has(subheadingId)) {
      this.activeCountryFilters.delete(subheadingId);
      this.countryFilterButtonTargets.forEach(btn => {
        if (btn.dataset.subheadingId === subheadingId) {
          btn.classList.remove("active");
          btn.setAttribute("aria-pressed", "false");
        }
      });
    }
    
    this.applyAllFilters();
  }

  toggleCountryFilter(event) {
    event.preventDefault();
    if (this.searchScope !== "current") return;

    const button = event.currentTarget;
    const { country, subheadingId } = button.dataset;
    this.activeSubheadingFilters.add(subheadingId);
    this.subheadingFilterButtonTargets.forEach(btn => {
      if (btn.dataset.subheadingId === subheadingId) btn.classList.add("active");
    });
    const countries = this.activeCountryFilters.get(subheadingId) || new Set();
    this.toggleSelection(countries, country, button);
    if (countries.size > 0) this.activeCountryFilters.set(subheadingId, countries);
    else this.activeCountryFilters.delete(subheadingId);
    this.applyAllFilters();
  }

  toggleSelection(selection, value, button) {
    if (selection.has(value)) {
      selection.delete(value);
      button.classList.remove("active");
    } else {
      selection.add(value);
      button.classList.add("active");
    }
    button.setAttribute("aria-pressed", String(selection.has(value)));
  }

  updateCountryFilterGroups() {
    this.countryFilterGroupTargets.forEach(group => {
      group.hidden = this.searchScope !== "current" || !this.activeSubheadingFilters.has(group.dataset.subheadingId);
    });
  }

  clearHeadingFilters() {
    this.activeHeadingFilters.clear();
    this.activeSubheadingFilters.clear();
    this.activeCountryFilters.clear();
    this.activeCompanyResearchFilter = false;
    [...this.headingFilterButtonTargets, ...this.subheadingFilterButtonTargets,
      ...this.countryFilterButtonTargets, ...this.companyResearchFilterButtonTargets].forEach(btn => {
      btn.classList.remove("active");
      btn.setAttribute("aria-pressed", "false");
    });
    this.applyAllFilters();
  }

  toggleCompanyResearchFilter(event) {
    event.preventDefault();
    const button = event.currentTarget;
    
    if (this.searchScope === "all") {
      // For all editions scope, redirect to global search with "Company Research"
      this.redirectToGlobalSearch("Company Research");
      return;
    }
    
    this.activeCompanyResearchFilter = !this.activeCompanyResearchFilter;
    if (this.activeCompanyResearchFilter) button.classList.add("active");
    else button.classList.remove("active");
    button.setAttribute("aria-pressed", String(this.activeCompanyResearchFilter));
    
    this.applyAllFilters();
  }

  toggleTitleColorFilter(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const titleColor = button.dataset.titleColor;
    const searchTerm = titleColor === "red" ? "Sells" : "Buys";

    if (this.searchScope === "all") {
      this.redirectToGlobalSearch(searchTerm);
      return;
    }

    this.toggleSelection(this.activeTitleColorFilters, titleColor, button);

    this.applyAllFilters();
  }

  applyFilters() {
    if (this.searchScope === "all") {
      // Don't apply local filtering when in "all editions" mode
      return;
    }
    
    if (this.activeFilters.size === 0) {
      // Show all articles
      this.articleTargets.forEach(article => {
        article.style.display = "block";
      });
      // Show all subheadings
      document.querySelectorAll('.sub-section').forEach(section => {
        section.style.display = "block";
      });
      this.showContent();
    } else {
      // Filter articles
      this.articleTargets.forEach(article => {
        const articleIndustries = article.dataset.industries ? 
          article.dataset.industries.split(',').map(i => i.trim()) : [];
        
        const shouldShow = articleIndustries.some(industry => 
          this.activeFilters.has(industry)
        );
        
        article.style.display = shouldShow ? "block" : "none";
      });
      
      // Hide empty subheadings
      document.querySelectorAll('.sub-section').forEach(section => {
        const visibleArticles = section.querySelectorAll('[data-industry-filter-target="article"]:not([style*="display: none"])');
        section.style.display = visibleArticles.length > 0 ? "block" : "none";
      });

      // Check if there are any visible articles after filtering
      const visibleArticles = this.articleTargets.filter(article => 
        article.style.display !== "none"
      );
      
      if (visibleArticles.length === 0) {
        this.showNoMatches();
      } else {
        this.showContent();
      }
    }
  }

  applyAllFilters() {
    this.updateCountryFilterGroups();
    const hasIndustryFilters = this.activeFilters.size > 0;
    const hasHeadingFilters = this.activeHeadingFilters.size > 0;
    const hasSubheadingFilters = this.activeSubheadingFilters.size > 0;
    const hasTitleColorFilters = this.activeTitleColorFilters.size > 0;
    const hasCompanyResearchFilter = this.activeCompanyResearchFilter;

    if (this.searchScope === "all" && !hasTitleColorFilters) {
      // Don't apply local filtering when in "all editions" mode, except for title color.
      return;
    }
    
    if (!hasIndustryFilters && !hasHeadingFilters && !hasSubheadingFilters && !hasTitleColorFilters && !hasCompanyResearchFilter) {
      // Show all articles and sections
      this.articleTargets.forEach(article => {
        article.style.display = "block";
      });
      document.querySelectorAll('.sub-section').forEach(section => {
        section.style.display = "block";
      });
      document.querySelectorAll('h3.fw-bold').forEach(heading => {
        heading.style.display = "block";
      });
      this.showContent();
      return;
    }
    
    // First apply heading/subheading/company research filters to sections
    document.querySelectorAll('.sub-section').forEach(section => {
      const sectionId = section.id.replace('subheading-', '');
      let shouldShowSection = true;
      
      if (hasSubheadingFilters || hasHeadingFilters || hasCompanyResearchFilter) {
        const sectionButton = this.subheadingFilterButtonTargets.find(btn => btn.dataset.subheadingId === sectionId);
        const headingId = section.dataset.headingId || (sectionButton && sectionButton.dataset.headingId);
        const isCompanyResearch = sectionButton && sectionButton.dataset.headingTitle === "Geography";
        // Section selections are alternatives; sectors and recommendation filters narrow their results.
        shouldShowSection = this.activeSubheadingFilters.has(sectionId) ||
          this.activeHeadingFilters.has(headingId) || (hasCompanyResearchFilter && isCompanyResearch);
      }

      section.style.display = shouldShowSection ? "block" : "none";
    });
    
    // Then apply industry filters within visible sections
    this.articleTargets.forEach(article => {
      const parentSection = article.closest('.sub-section');
      const hasSectionFilters = hasHeadingFilters || hasSubheadingFilters || hasCompanyResearchFilter;
      const sectionVisible = parentSection ? parentSection.style.display !== "none" : !hasSectionFilters;
      
      let shouldShowArticle = true;
      
      if (hasIndustryFilters && sectionVisible) {
        const articleIndustries = article.dataset.industries ? 
          article.dataset.industries.split(',').map(i => i.trim()) : [];
        shouldShowArticle = articleIndustries.some(industry => 
          this.activeFilters.has(industry)
        );
      } else if (!hasIndustryFilters) {
        shouldShowArticle = sectionVisible;
      } else {
        shouldShowArticle = false;
      }

      const sectionId = parentSection ? parentSection.id.replace('subheading-', '') : article.dataset.subheadingId;
      const countries = this.activeCountryFilters.get(sectionId);
      if (shouldShowArticle && countries && countries.size > 0) {
        shouldShowArticle = countries.has(article.dataset.country);
      }

      if (shouldShowArticle && hasTitleColorFilters) {
        const articleTitleColors = article.dataset.titleColors ?
          article.dataset.titleColors.split(',').map(color => color.trim()).filter(Boolean) : [];
        shouldShowArticle = articleTitleColors.some(color =>
          this.activeTitleColorFilters.has(color)
        );
      }
      
      article.style.display = shouldShowArticle ? "block" : "none";
    });
    
    // Hide empty sections after article filtering
    document.querySelectorAll('.sub-section').forEach(section => {
      if (section.style.display !== "none") {
        const visibleArticles = section.querySelectorAll('[data-industry-filter-target="article"]:not([style*="display: none"])');
        section.style.display = visibleArticles.length > 0 ? "block" : "none";
      }
    });
    
    // Update blue headings visibility based on visible sections
    const visibleSections = Array.from(document.querySelectorAll('.sub-section')).filter(
      section => section.style.display !== "none"
    );
    const visibleHeadingIds = new Set(
      visibleSections.map(section => section.dataset.headingId).filter(Boolean)
    );
    
    // Check if any visible sections are ESG or Commodities (they have their own blue headings)
    const hasEsgOrCommoditiesVisible = visibleSections.some(section => {
      const blueHeadingInSection = section.querySelector('.blue-heading');
      if (blueHeadingInSection) {
        const headingText = blueHeadingInSection.textContent.trim();
        return headingText === "ESG" || headingText === "Commodities";
      }
      return false;
    });
    
    document.querySelectorAll('.blue-heading').forEach(blueHeading => {
      // Check if this blue heading is inside a section
      const parentSection = blueHeading.closest('.sub-section');
      
      if (parentSection) {
        // This is a subheading blue heading (like ESG or Commodities)
        blueHeading.style.display = parentSection.style.display;
      } else {
        // This is a top-level heading blue heading (like Macro Research or Company Research)
        const headingText = blueHeading.textContent.trim();
        
        // Find if any visible section belongs to this heading
        let shouldShow = false;
        
        if (headingText === "Macro Research") {
          // Find the Macro Research heading button to get its ID
          const macroButton = this.headingFilterButtonTargets.find(btn => 
            btn.textContent.trim() === "Macro Research"
          );
          const macroHeadingId = macroButton ? macroButton.dataset.headingId : null;
          shouldShow = macroHeadingId && visibleHeadingIds.has(macroHeadingId);
        } else if (headingText === "Company Research") {
          // Company Research corresponds to Geography heading (not Macro Research)
          // BUT: Don't show if only ESG or Commodities are visible (they have their own blue headings)
          const macroButton = this.headingFilterButtonTargets.find(btn => 
            btn.textContent.trim() === "Macro Research"
          );
          const macroHeadingId = macroButton ? macroButton.dataset.headingId : null;
          
          // Show if there are any visible sections that don't belong to Macro Research
          // AND either ESG/Commodities are not the only visible sections
          const hasNonMacroSections = visibleSections.some(section => 
            section.dataset.headingId !== macroHeadingId
          );
          
          // Only show Company Research if we have non-Macro sections 
          // and it's not ONLY ESG or Commodities sections
          shouldShow = hasNonMacroSections && !hasEsgOrCommoditiesVisible;
        }
        
        blueHeading.style.display = shouldShow ? "block" : "none";
      }
    });
    
    // Hide empty headings
    document.querySelectorAll('h3.fw-bold').forEach(heading => {
      const nextSibling = heading.nextElementSibling;
      let hasVisibleSections = false;
      
      let current = nextSibling;
      while (current && !current.classList.contains('fw-bold')) {
        if (current.classList.contains('sub-section') && current.style.display !== "none") {
          hasVisibleSections = true;
          break;
        }
        current = current.nextElementSibling;
      }
      
      heading.style.display = hasVisibleSections ? "block" : "none";
    });

    // Check if there are any visible articles after filtering
    const visibleArticles = this.articleTargets.filter(article => 
      article.style.display !== "none"
    );
    
    if (visibleArticles.length === 0) {
      this.showNoMatches();
    } else {
      this.showContent();
    }
    
    // Scroll to top of articles content
    this.scrollToTop();
  }
  
  scrollToTop() {
    const editionContainer = document.getElementById('edition-view-container');
    if (editionContainer) {
      editionContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  canApplyFilterInCurrentEdition(searchTerm) {
    if (!searchTerm) return false;
    
    const decodedSearchTerm = decodeURIComponent(searchTerm).toLowerCase();
    
    // Check if it's an industry filter that exists in current edition
    const hasIndustryFilter = this.filterButtonTargets.some(btn => {
      const industry = btn.dataset.industry;
      return industry && industry.toLowerCase() === decodedSearchTerm;
    });
    
    if (hasIndustryFilter) return true;
    
    // Check if it's a heading filter that exists in current edition
    if (this.headingFilterButtonTargets) {
      const hasHeadingFilter = this.headingFilterButtonTargets.some(btn => {
        const headingTitle = btn.textContent.trim().toLowerCase();
        return headingTitle === decodedSearchTerm;
      });
      
      if (hasHeadingFilter) return true;
    }
    
    // Check if it's a subheading filter that exists in current edition
    if (this.subheadingFilterButtonTargets) {
      const hasSubheadingFilter = this.subheadingFilterButtonTargets.some(btn => {
        const subheadingTitle = btn.textContent.trim().toLowerCase();
        return subheadingTitle === decodedSearchTerm;
      });
      
      if (hasSubheadingFilter) return true;
    }
    
    // Check if it's company research filter
    if (decodedSearchTerm === "company research") {
      return this.companyResearchFilterButtonTargets.length > 0;
    }

    const expectedColor = this.titleColorForFilterTerm(decodedSearchTerm);
    if (expectedColor) {
      return this.articleTargets.some(article => {
        const articleTitleColors = article.dataset.titleColors ?
          article.dataset.titleColors.split(',').map(color => color.trim()).filter(Boolean) : [];
        return articleTitleColors.includes(expectedColor);
      });
    }
    
    return false;
  }

  applyFilterInCurrentEdition(filterTerm) {
    if (!filterTerm) return;
    
    const decodedFilterTerm = decodeURIComponent(filterTerm).toLowerCase();
    
    // Try to apply as industry filter
    this.filterButtonTargets.forEach(btn => {
      const industry = btn.dataset.industry;
      if (industry && industry.toLowerCase() === decodedFilterTerm) {
        btn.classList.add("active");
        this.activeFilters.add(industry);
      }
    });
    
    // Try to apply as heading filter
    if (this.headingFilterButtonTargets) {
      this.headingFilterButtonTargets.forEach(btn => {
        const headingTitle = btn.textContent.trim().toLowerCase();
        if (headingTitle === decodedFilterTerm) {
          btn.classList.add("active");
          this.activeHeadingFilters.add(btn.dataset.headingId);
        }
      });
    }
    
    // Try to apply as subheading filter
    if (this.subheadingFilterButtonTargets) {
      this.subheadingFilterButtonTargets.forEach(btn => {
        const subheadingTitle = btn.textContent.trim().toLowerCase();
        if (subheadingTitle === decodedFilterTerm) {
          btn.classList.add("active");
          this.activeSubheadingFilters.add(btn.dataset.subheadingId);
        }
      });
    }
    
    // Try to apply as company research filter
    if (decodedFilterTerm === "company research") {
      this.activeCompanyResearchFilter = true;
      if (this.companyResearchFilterButtonTargets.length > 0) {
        this.companyResearchFilterButtonTargets[0].classList.add("active");
      }
    }

    this.markActiveTitleColorFilter(decodedFilterTerm);
    
    // Apply the filters to show/hide articles
    this.applyAllFilters();
    
    // Check if industry dropdown should be open
    this.checkIndustryDropdownState();
  }

  showNoMatches() {
    // Hide articles content and show no matches message
    if (this.hasArticlesContentTarget) {
      this.articlesContentTarget.style.display = "none";
    }
    if (this.hasNoMatchesMessageTarget) {
      this.noMatchesMessageTarget.style.display = "block";
    }
  }

  showContent() {
    // Show articles content and hide no matches message
    if (this.hasArticlesContentTarget) {
      this.articlesContentTarget.style.display = "block";
    }
    if (this.hasNoMatchesMessageTarget) {
      this.noMatchesMessageTarget.style.display = "none";
    }
  }

  markActiveTitleColorFilter(filterTerm) {
    const titleColor = this.titleColorForFilterTerm(filterTerm);
    if (!titleColor) return;

    this.activeTitleColorFilters.add(titleColor);

    if (this.titleColorFilterButtonTargets) {
      this.titleColorFilterButtonTargets.forEach(btn => {
        if (btn.dataset.titleColor === titleColor) {
          btn.classList.add("active");
        }
      });
    }
  }

  titleColorForFilterTerm(filterTerm) {
    const titleColorMap = {
      "blue articles": "blue",
      "red articles": "red",
      "buys": "blue",
      "sells": "red"
    };

    return titleColorMap[filterTerm];
  }
}
